import { useId, useState } from "react";
import axios from "axios";
import { Check, CircleAlert } from "lucide-react";
import { PdfPreview } from "@/components/common/PdfPreview";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import axiosInstance from "../../api/axiosConfig";

type ModalStep = "upload" | "review";

const MAX_URL_LENGTH = 2083;
const HTTPS_PREFIX = "https://";

/** Hostnames that may expose internal services (SSRF). */
const BLOCKED_HOSTNAMES = new Set([
  "localhost",
  "metadata",
  "metadata.google.internal",
  "169.254.169.254"
]);

/** Check if IPv4 is in a private or SSRF-risk range. */
function isPrivateOrReservedIPv4(parts: number[]): boolean {
  if (parts.length !== 4) return true;
  const [a, b, c] = parts;
  if (a === 0) return true; // 0.0.0.0/8
  if (a === 127) return true; // loopback
  if (a === 10) return true; // 10.0.0.0/8
  if (a === 169 && b === 254) return true; // link-local
  if (a === 172 && b >= 16 && b <= 31) return true; // 172.16.0.0/12
  if (a === 192 && b === 168) return true; // 192.168.0.0/16
  if (a >= 224) return true; // multicast / reserved
  return false;
}

/** Check if hostname is a private/internal IP or blocked name (SSRF mitigation). */
function isBlockedHost(hostname: string): boolean {
  const lower = hostname.toLowerCase().replace(/^\[|\]$/g, "");
  if (BLOCKED_HOSTNAMES.has(lower)) return true;
  if (lower.endsWith(".local") || lower.endsWith(".internal")) return true;
  // IPv4
  const ipv4Match = lower.match(/^(\d+)\.(\d+)\.(\d+)\.(\d+)$/);
  if (ipv4Match) {
    const parts = ipv4Match.slice(1, 5).map(Number);
    if (parts.every((n) => n >= 0 && n <= 255))
      return isPrivateOrReservedIPv4(parts);
  }
  // IPv6 loopback / link-local / unique local
  if (lower === "[::1]" || lower === "::1") return true;
  if (
    lower.startsWith("fe80:") ||
    lower.startsWith("fc") ||
    lower.startsWith("fd")
  )
    return true;
  if (
    lower.startsWith("[fe80:") ||
    lower.startsWith("[fc") ||
    lower.startsWith("[fd")
  )
    return true;
  return false;
}

/** Client-side URL validation to reduce risk of malicious or SSRF-prone URLs. */
function validateDocumentUrl(
  input: string
): { valid: true } | { valid: false; error: string } {
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return { valid: false, error: "Please enter a URL." };
  }
  if (trimmed.length > MAX_URL_LENGTH) {
    return {
      valid: false,
      error: `URL must be ${MAX_URL_LENGTH} characters or less.`
    };
  }
  if (!trimmed.toLowerCase().startsWith(HTTPS_PREFIX)) {
    return {
      valid: false,
      error: "Only HTTPS URLs are allowed."
    };
  }
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return { valid: false, error: "Please enter a valid URL." };
  }
  const host = parsed.hostname;
  if (!host || isBlockedHost(host)) {
    return {
      valid: false,
      error: "This URL is not allowed for security reasons."
    };
  }
  return { valid: true };
}

function base64PdfToBlob(base64: string): Blob {
  const byteChars = atob(base64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i += 1) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: "application/pdf" });
}

function pdfBlobToFile(pdfBlob: Blob, filename: string): File {
  return new File([pdfBlob], filename, { type: "application/pdf" });
}

function filenameFromUrl(url: string): string {
  try {
    const { pathname } = new URL(url);
    const segment = pathname.split("/").filter(Boolean).pop();
    if (segment && /\.pdf$/i.test(segment)) return segment;
    if (segment) return `${segment}.pdf`;
  } catch {
    // ignore
  }
  return "url-document.pdf";
}

export type UploadUrlDocumentModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the PDF file to run the same upload flow as Google Drive (Firebase + backend). */
  onConfirmSubmit?: (files: File[]) => void;
};

export function UploadUrlDocumentModal({
  isOpen,
  onClose,
  onConfirmSubmit
}: UploadUrlDocumentModalProps) {
  const [currentStep, setCurrentStep] = useState<ModalStep>("upload");
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const urlInputId = useId();

  const handleClose = (open: boolean) => {
    if (!open) {
      setCurrentStep("upload");
      setUrl("");
      setPdfBlob(null);
      setLoading(false);
      setUploadError(null);
      setSubmitError(null);
      onClose();
    }
  };

  const handleUpload = async () => {
    setUploadError(null);
    const validation = validateDocumentUrl(url);
    if (!validation.valid) {
      setUploadError(validation.error);
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.post<string>("/documents/upload", {
        url: url.trim()
      });
      const base64 =
        typeof response.data === "string" ? response.data.trim() : "";
      if (!base64) {
        throw new Error(
          "The URL could not be converted into a previewable PDF."
        );
      }
      const nextPdfBlob = base64PdfToBlob(base64);
      setPdfBlob(nextPdfBlob);
      setCurrentStep("review");
    } catch (error: unknown) {
      const fallback =
        error instanceof Error
          ? error.message
          : "Failed to convert URL to PDF. Please try again.";
      let message: string = fallback;
      if (axios.isAxiosError(error) && error.response?.data != null) {
        const { data } = error.response;
        if (typeof data === "string") {
          message = data;
        } else if (typeof data === "object" && data !== null) {
          const obj = data as Record<string, unknown>;
          if (typeof obj.error === "string") {
            message = obj.error;
          } else if (typeof obj.message === "string") {
            message = obj.message;
          }
        }
      }
      setUploadError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (!pdfBlob || !onConfirmSubmit) return;
    setSubmitError(null);
    setLoading(true);
    try {
      const filename = filenameFromUrl(url) || "url-document.pdf";
      const file = pdfBlobToFile(pdfBlob, filename);
      onConfirmSubmit([file]);
      handleClose(false);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to add document for upload.";
      setSubmitError(message);
    } finally {
      setLoading(false);
    }
  };

  const getStepVariant = (
    step: ModalStep
  ): "active" | "completed" | "default" => {
    const variants: Record<
      ModalStep,
      Record<ModalStep, "active" | "completed" | "default">
    > = {
      upload: { upload: "active", review: "completed" },
      review: { upload: "default", review: "active" }
    };
    return variants[step][currentStep] ?? "default";
  };

  const renderStep = (step: ModalStep, stepNumber: number, label: string) => {
    const variant = getStepVariant(step);
    return (
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "flex size-6 items-center justify-center rounded-full text-sm",
            variant === "active" && "bg-primary text-primary-foreground",
            variant === "completed" && "bg-accent-soft text-accent-seed",
            variant === "default" && "bg-fill-2 text-fg-3"
          )}
        >
          {variant === "completed" ? (
            <Check className="size-3.5" />
          ) : (
            stepNumber
          )}
        </span>
        <span
          className={cn(
            "text-base",
            variant === "active" ? "font-medium text-fg" : "text-fg-3"
          )}
        >
          {label}
        </span>
      </div>
    );
  };

  const renderError = (title: string, description: string) => (
    <Alert
      variant="destructive"
      className="w-full border-danger-line bg-danger-soft"
    >
      <CircleAlert />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>{description}</AlertDescription>
    </Alert>
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="z-1400 gap-6 p-6 sm:max-w-175">
        <DialogHeader>
          <DialogTitle className="text-lg">Convert URL to PDF</DialogTitle>
        </DialogHeader>

        <div className="flex w-full items-center gap-3">
          {renderStep("upload", 1, "Upload")}
          <div className="h-px flex-1 bg-line-2" />
          {renderStep("review", 2, "Review")}
        </div>

        {currentStep === "upload" && (
          <>
            {uploadError && renderError("Upload Failed", uploadError)}
            <div className="flex w-full flex-col items-start gap-1.5">
              <Label htmlFor={urlInputId}>Document URL</Label>
              <Input
                id={urlInputId}
                placeholder="https://example.com/document.pdf"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                maxLength={MAX_URL_LENGTH}
                type="url"
                autoComplete="url"
              />
            </div>
            <div className="flex w-full items-center justify-end gap-2">
              {loading ? (
                <Spinner />
              ) : (
                <Button
                  variant="primary"
                  disabled={!url.trim()}
                  onClick={handleUpload}
                >
                  Upload
                </Button>
              )}
            </div>
          </>
        )}

        {currentStep === "review" && (
          <>
            {submitError && renderError("Submit Failed", submitError)}
            <div className="flex h-100 w-full overflow-hidden rounded-lg ring-1 ring-line-2">
              {pdfBlob ? (
                <PdfPreview file={pdfBlob} ariaLabel="PDF Preview" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-base text-fg-3">
                  No preview available
                </div>
              )}
            </div>
            <div className="flex w-full items-center justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setCurrentStep("upload");
                  setSubmitError(null);
                }}
              >
                Back
              </Button>
              {loading ? (
                <Spinner />
              ) : (
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={!onConfirmSubmit}
                >
                  Submit
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
